// filepath: apps/web/src/features/admin/templates/update/ConflictResolutionTable.tsx
//
// FR-UI-ADM-12 AC-3 / AC-4 — per-field resolution radios.
//
// Renders one row per FieldClassification with the three
// classifications (unchanged / modified / added) and the three
// resolutions (keep_tenant / accept_update / merge). The merge
// preview is opened via the onOpenMergePreview handler.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { ConflictResolutionChoice, FieldClassification } from "./types";
import type { ResolutionMap } from "./TemplateUpdatePage";

interface ConflictResolutionTableProps {
  fields: FieldClassification[];
  resolutions: ResolutionMap;
  onResolve: (
    key: string,
    choice: ConflictResolutionChoice,
    mergedValue?: unknown,
  ) => void;
  mergePreviewField: string | null;
  onOpenMergePreview: (key: string) => void;
  onCloseMergePreview: () => void;
}

export function ConflictResolutionTable({
  fields,
  resolutions,
  onResolve,
  mergePreviewField,
  onOpenMergePreview,
  onCloseMergePreview,
}: ConflictResolutionTableProps): React.ReactElement {
  const { t } = useTranslation();

  const conflicts = fields.filter(
    (f) =>
      f.classification === "modified_by_tenant" ||
      f.classification === "added_by_tenant",
  );

  if (conflicts.length === 0) {
    return (
      <div
        className="rounded border border-border bg-background p-6 text-center text-sm text-muted-foreground"
        data-testid="template-update-resolution-empty"
      >
        {t("admin.templates.update.list.empty")}
      </div>
    );
  }

  return (
    <div
      className="overflow-x-auto rounded border border-border bg-background"
      data-testid="template-update-resolution-table"
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2">Field</th>
            <th className="px-3 py-2">Classification</th>
            <th className="px-3 py-2">Resolution</th>
          </tr>
        </thead>
        <tbody>
          {conflicts.map((f) => {
            const key = `${f.entity_type}.${f.field_name}`;
            const r = resolutions[key];
            return (
              <tr
                key={key}
                className="border-b border-border last:border-0"
                data-testid="template-update-resolution-row"
                data-field-key={key}
                data-classification={f.classification}
              >
                <td className="px-3 py-2 font-mono text-xs">{key}</td>
                <td className="px-3 py-2 text-xs">
                  <span
                    className={
                      f.classification === "modified_by_tenant"
                        ? "inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
                        : "inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
                    }
                    data-state={f.classification}
                  >
                    <span aria-hidden="true">⚠</span>
                    <span>
                      {t(
                        `admin.templates.update.classification.${f.classification === "modified_by_tenant" ? "modified" : "added"}`,
                      )}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div
                    className="flex items-center gap-2"
                    role="radiogroup"
                    aria-label={key}
                  >
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="radio"
                        name={`res-${key}`}
                        value="keep_tenant"
                        checked={r?.choice === "keep_tenant"}
                        onChange={() => onResolve(key, "keep_tenant")}
                        data-testid="template-update-resolution-keep"
                      />
                      {t("admin.templates.update.resolution.keep_tenant")}
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="radio"
                        name={`res-${key}`}
                        value="accept_update"
                        checked={r?.choice === "accept_update"}
                        onChange={() => onResolve(key, "accept_update")}
                        data-testid="template-update-resolution-accept"
                      />
                      {t("admin.templates.update.resolution.accept_update")}
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="radio"
                        name={`res-${key}`}
                        value="merge"
                        checked={r?.choice === "merge"}
                        onChange={() => {
                          onResolve(key, "merge");
                          onOpenMergePreview(key);
                        }}
                        data-testid="template-update-resolution-merge"
                      />
                      {t("admin.templates.update.resolution.merge")}
                    </label>
                  </div>
                  {mergePreviewField === key ? (
                    <div
                      className="mt-2 rounded border border-blue-300 bg-blue-50 p-2 text-xs"
                      data-testid="template-update-merge-preview"
                    >
                      <p className="text-muted-foreground">
                        {t("admin.templates.update.resolution.preview")}
                      </p>
                      <button
                        type="button"
                        className="mt-1 text-xs text-blue-700 underline"
                        onClick={onCloseMergePreview}
                        data-testid="template-update-merge-close"
                      >
                        {t("admin.templates.update.resolution.close")}
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
